"use client";

import { useEffect, useState } from "react";
import "./cookie-consent.css";
import {
  buildConsent,
  COOKIE_CONSENT_EVENT,
  COOKIE_SETTINGS_EVENT,
  type CookieConsent,
  openCookieSettings,
  readCookieConsent,
  writeCookieConsent,
} from "@/lib/cookie-consent";

type PreferenceKey = "analytics" | "marketing" | "personalization";

export default function CookieConsentBanner() {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [draft, setDraft] = useState<Omit<CookieConsent, "essential" | "updatedAt">>({
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    const existing = readCookieConsent();
    if (existing) {
      setConsent(existing);
      setDraft({
        analytics: existing.analytics,
        marketing: existing.marketing,
        personalization: existing.personalization,
      });
    }
    setIsLoaded(true);

    const handleConsentChanged = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsent>).detail;
      if (!detail) return;
      setConsent(detail);
      setDraft({
        analytics: detail.analytics,
        marketing: detail.marketing,
        personalization: detail.personalization,
      });
    };

    const handleOpenSettings = () => setIsManaging(true);

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChanged as EventListener);
    window.addEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);

    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_EVENT,
        handleConsentChanged as EventListener
      );
      window.removeEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  if (!isLoaded) return null;

  const saveConsent = (next: CookieConsent) => {
    writeCookieConsent(next);
    setConsent(next);
    setIsManaging(false);
  };

  const acceptAll = () =>
    saveConsent(
      buildConsent({
        analytics: true,
        marketing: true,
        personalization: true,
      })
    );

  const rejectNonEssential = () =>
    saveConsent(
      buildConsent({
        analytics: false,
        marketing: false,
        personalization: false,
      })
    );

  const savePreferences = () => saveConsent(buildConsent(draft));

  const toggleDraft = (key: PreferenceKey) => {
    setDraft((current) => ({ ...current, [key]: !current[key] }));
  };

  const showBanner = !consent;
  const showSettingsButton = Boolean(consent);

  return (
    <>
      {showBanner ? (
        <div className="ctribe-cookie-banner" role="dialog" aria-label="Cookie consent">
          <div className="ctribe-cookie-banner__content">
            <p className="ctribe-cookie-banner__eyebrow">Privacy Choices</p>
            <h2 className="ctribe-cookie-banner__title">Your privacy matters here.</h2>
            <p className="ctribe-cookie-banner__copy">
              We use essential cookies to keep the site working and optional cookies
              for analytics, marketing, and personalization. Your choice applies on
              every C-Tribe site. Read our{" "}
              <a href="https://www.ctribefestival.com/privacy" className="ctribe-cookie-banner__link">
                Privacy Policy
              </a>
              .
            </p>
          </div>
          <div className="ctribe-cookie-banner__actions">
            <button
              type="button"
              className="ctribe-cookie-btn ctribe-cookie-btn--ghost"
              onClick={() => setIsManaging(true)}
            >
              Manage Preferences
            </button>
            <button
              type="button"
              className="ctribe-cookie-btn ctribe-cookie-btn--secondary"
              onClick={rejectNonEssential}
            >
              Reject Non-Essential
            </button>
            <button
              type="button"
              className="ctribe-cookie-btn ctribe-cookie-btn--primary"
              onClick={acceptAll}
            >
              Accept All
            </button>
          </div>
        </div>
      ) : null}

      {showSettingsButton ? (
        <button
          type="button"
          className="ctribe-cookie-settings-trigger"
          onClick={openCookieSettings}
        >
          Cookie Settings
        </button>
      ) : null}

      {isManaging ? (
        <div className="ctribe-cookie-modal" role="dialog" aria-modal="true">
          <div
            className="ctribe-cookie-modal__backdrop"
            onClick={() => setIsManaging(false)}
          />
          <div className="ctribe-cookie-modal__panel">
            <div className="ctribe-cookie-modal__header">
              <div>
                <p className="ctribe-cookie-banner__eyebrow">Manage Preferences</p>
                <h2 className="ctribe-cookie-banner__title">Cookie settings</h2>
              </div>
              <button
                type="button"
                className="ctribe-cookie-close"
                onClick={() => setIsManaging(false)}
                aria-label="Close cookie settings"
              >
                Close
              </button>
            </div>

            <div className="ctribe-cookie-pref">
              <div>
                <h3>Essential</h3>
                <p>Required for site functionality and security.</p>
              </div>
              <span className="ctribe-cookie-pref__fixed">Always on</span>
            </div>

            <PreferenceRow
              title="Analytics"
              description="Helps us understand site performance and usage patterns."
              checked={draft.analytics}
              onToggle={() => toggleDraft("analytics")}
            />
            <PreferenceRow
              title="Marketing"
              description="Supports campaign measurement and remarketing."
              checked={draft.marketing}
              onToggle={() => toggleDraft("marketing")}
            />
            <PreferenceRow
              title="Personalization"
              description="Remembers preferences and improves repeat visits."
              checked={draft.personalization}
              onToggle={() => toggleDraft("personalization")}
            />

            <div className="ctribe-cookie-modal__actions">
              <button
                type="button"
                className="ctribe-cookie-btn ctribe-cookie-btn--ghost"
                onClick={rejectNonEssential}
              >
                Reject Non-Essential
              </button>
              <button
                type="button"
                className="ctribe-cookie-btn ctribe-cookie-btn--primary"
                onClick={savePreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PreferenceRow({
  title,
  description,
  checked,
  onToggle,
}: {
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="ctribe-cookie-pref">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <button
        type="button"
        className={`ctribe-cookie-toggle ${checked ? "is-on" : ""}`}
        aria-pressed={checked}
        onClick={onToggle}
      >
        <span />
      </button>
    </div>
  );
}
